import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  MessageSquare,
  Brain,
  FileQuestion,
  ClipboardList,
  BookOpen,
  Headphones,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { Button, Card, CardBody } from '@/components/ui';
import { useAuthStore } from '@/stores';
import kodaLogo from '../../assets/koda-orange.png';

const FEATURES = [
  {
    icon: MessageSquare,
    title: 'AI Chat with Citations',
    description: 'Ask questions about your documents and get answers with source references',
    color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
  },
  {
    icon: Brain,
    title: 'Smart Flashcards',
    description: 'AI-generated flashcards with spaced repetition for effective learning',
    color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
  },
  {
    icon: FileQuestion,
    title: 'Quiz Generation',
    description: 'Auto-generate quizzes from your study materials instantly',
    color: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
  },
  {
    icon: ClipboardList,
    title: 'Practice Tests',
    description: 'Timed assessments with difficulty levels and detailed scoring',
    color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
  },
  {
    icon: BookOpen,
    title: 'Lecture Notes',
    description: 'AI-generated structured lecture content from your documents',
    color: 'bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400',
  },
  {
    icon: Headphones,
    title: 'Podcast Generation',
    description: 'Convert your documents into engaging audio content',
    color: 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400',
  },
];

export function LandingPage() {
  const { user, isLoading } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background-primary flex items-center justify-center">
        <div className="animate-pulse">
          <Sparkles size={48} className="text-accent-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-primary">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background-primary/80 backdrop-blur-sm border-b border-border-subtle">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src={kodaLogo} alt="Koda" className="h-8" />
              <span className="font-serif text-2xl text-accent-primary">Koda</span>
            </Link>
            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" size="sm">
                  Log in
                </Button>
              </Link>
              <Link to="/signup">
                <Button size="sm">
                  Sign up
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <img src={kodaLogo} alt="Koda" className="h-24 sm:h-32 mx-auto mb-8" />
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-foreground-primary mb-6">
            Study smarter with{' '}
            <span className="text-accent-primary">AI-powered</span> learning
          </h1>
          <p className="text-lg sm:text-xl text-foreground-secondary max-w-2xl mx-auto mb-10">
            Transform your documents into flashcards, quizzes, podcasts, and more.
            Koda helps you learn faster with intelligent study tools.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/signup">
              <Button size="lg" className="w-full sm:w-auto">
                Get started free
                <ArrowRight size={18} />
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="secondary" size="lg" className="w-full sm:w-auto">
                I already have an account
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-background-secondary">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl sm:text-4xl text-foreground-primary mb-4">
              Everything you need to excel
            </h2>
            <p className="text-foreground-secondary text-lg max-w-2xl mx-auto">
              Powerful AI tools that transform how you study
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((feature) => (
              <Card key={feature.title} className="hover:shadow-card transition-shadow">
                <CardBody>
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${feature.color}`}>
                    <feature.icon size={24} />
                  </div>
                  <h3 className="font-medium text-lg mb-2">{feature.title}</h3>
                  <p className="text-sm text-foreground-muted">{feature.description}</p>
                </CardBody>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-3xl sm:text-4xl text-foreground-primary mb-4">
            Ready to transform your studying?
          </h2>
          <p className="text-foreground-secondary text-lg mb-8">
            Join thousands of students learning smarter with Koda
          </p>
          <Link to="/signup">
            <Button size="lg">
              Start learning for free
              <ArrowRight size={18} />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 border-t border-border-subtle">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src={kodaLogo} alt="Koda" className="h-6" />
            <span className="font-serif text-xl text-accent-primary">Koda</span>
          </div>
          <p className="text-sm text-foreground-muted">
            &copy; {new Date().getFullYear()} Koda. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
